/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { AdBreakInfo, AdContentTimePosition, AdInfo } from '@quickplay/player-interface';
import { BroadpeakRawAd, BroadpeakRawAdBreak } from '../model/BroadpeakRawAdModels';

const TIME_VALUE_UNKNOWN = -1;

/**
 * Returns value if it is a finite number, otherwise returns fallback.
 * Safely handles undefined, null, NaN, and non-number types.
 */
export function getValidNumber(value: unknown, fallback: number = TIME_VALUE_UNKNOWN): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Creates a new instance of AdBreakInfo.
 * @param adBreak the advert break
 * @param timePosition the content time position
 * @param adBreakIndex the advert break index
 */
export function createAdBreakInfo(
  adBreak: BroadpeakRawAdBreak,
  timePosition: AdContentTimePosition,
  adBreakIndex: number,
): AdBreakInfo {
  let adBreakStartTimeOffsetMs = TIME_VALUE_UNKNOWN;
  const calculatedAdBreakDuration = adBreak.ads?.reduce((total, ad) => total + Math.max(ad.duration || 0, 0), 0) ?? 0;

  const adBreakDuration = adBreak.duration >= 0 ? adBreak.duration : calculatedAdBreakDuration;

  const totalAds = adBreak.adCount >= 0 ? adBreak.adCount : (adBreak.ads?.length ?? 0);

  if (timePosition === AdContentTimePosition.PREROLL) {
    adBreakStartTimeOffsetMs = 0;
  } else if (typeof adBreak.startPosition === 'number') {
    adBreakStartTimeOffsetMs = adBreak.startPosition;
  }

  return {
    adBreakID: adBreak.id ?? '',

    contentTimePosition: timePosition,

    adSequencePosition: typeof adBreak.ads?.[0]?.index === 'number' ? adBreak.ads[0].index + 1 : 1,

    adBreakStartTimeOffsetMs,

    remainingTimeMs: adBreakDuration,

    durationMs: adBreakDuration,

    totalAds,

    adBreakIndex,
  };
}

/**
 * Creates a new instance of AdInfo.
 * @param ad the advert
 * @param activeAdBreakInfo the advert break that contains this advert
 */
export function createAdInfo(ad: BroadpeakRawAd, activeAdBreakInfo?: AdBreakInfo): AdInfo {
  const skipPosition = ad.skipPosition;
  const startPosition = ad.startPosition;
  const hasVastProperties = ad.creativeId != null || ad.clickURL != null;

  const isSkippable =
    typeof skipPosition === 'number' && typeof startPosition === 'number' && skipPosition > startPosition;

  const skipOffsetMs = isSkippable ? skipPosition - startPosition : TIME_VALUE_UNKNOWN;

  return {
    adID: ad.adId ?? '',

    sequence: typeof ad.index === 'number' ? ad.index + 1 : 1,

    adStartTimeOffsetMs: getValidNumber(ad.startPosition),

    durationMs: getValidNumber(ad.duration),

    remainingTimeMs: getValidNumber(ad.duration),

    isSkippable,

    skipOffsetMs,

    isFiller: false,

    adBreakInfo: activeAdBreakInfo,

    ...(hasVastProperties
      ? {
          vastProperties: {
            creativeId: ad.creativeId,
            videoClickThroughURL: ad.clickURL,
          },
        }
      : {}),

    delivery: { technology: 'server-side', stitcher: 'Broadpeak' },
  };
}

export function toAdContentTimePosition(timePosition: string): AdContentTimePosition {
  switch (timePosition) {
    case 'preroll':
      return AdContentTimePosition.PREROLL;
    case 'midroll':
      return AdContentTimePosition.MIDROLL;
    case 'postroll':
      return AdContentTimePosition.POSTROLL;
    default:
      return AdContentTimePosition.UNKNOWN;
  }
}

export function contentTimeToAdContentTimePosition(timeOffset: number): AdContentTimePosition {
  switch (timeOffset) {
    case 0:
      return AdContentTimePosition.PREROLL;
    case -1:
      return AdContentTimePosition.POSTROLL;
    default:
      return AdContentTimePosition.MIDROLL;
  }
}

export function getAdBreakIndex(timePosition: AdContentTimePosition, midrollAdBreakIndex: number): number {
  switch (timePosition) {
    case AdContentTimePosition.PREROLL:
      return 0;
    case AdContentTimePosition.MIDROLL:
      return ++midrollAdBreakIndex;
    case AdContentTimePosition.POSTROLL:
      return -1;
    default:
      return -1;
  }
}
